/**
 * HYDRATION — single source of truth for the personalized daily target.
 *
 * Rule: recommended_ml_per_day = weight_kg × 35
 * Example: 70 kg × 35 = 2450 ml = 2.45 L
 *
 * - Uses the user's LATEST available weight. No weight → no target (never
 *   invent one).
 * - Weight in pounds is converted to kilograms before calculating.
 * - When the current weight changes, today's target recalculates. Past days
 *   keep the target they were recorded with (`HydrationDayRecord.targetMl`),
 *   so history is never rewritten retroactively.
 * - This is a general baseline, not a medical prescription.
 */
import { toKg } from './units'

export const HYDRATION_ML_PER_KG = 35

/** Quick-add amounts offered in the Daily Check. */
export const HYDRATION_QUICK_ADD_ML = [250, 500] as const

/** A stored day of hydration. The target is frozen with the day. */
export interface HydrationDayRecord {
  date: string
  intakeMl: number
  targetMl: number | null
}

export function hydrationTargetMl(
  weight: number | null | undefined,
  unit: 'kg' | 'lb' = 'kg'
): number | null {
  if (weight == null || !Number.isFinite(weight) || weight <= 0) return null
  const kg = toKg(weight, unit)
  // Rounded to 10 ml so the displayed litres stay readable.
  return Math.round((kg * HYDRATION_ML_PER_KG) / 10) * 10
}

/** "1,4 L" / "2,45 L" — litres with up to two decimals in the active locale. */
export function formatLiters(ml: number, locale: string): string {
  const liters = ml / 1000
  return `${liters.toLocaleString(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 2,
  })} L`
}

export function hydrationRatio(intakeMl: number, targetMl: number | null): number {
  if (!targetMl) return 0
  return Math.min(1, Math.max(0, intakeMl / targetMl))
}
